import { useState, useRef, useEffect } from 'react';
import type { PodcastWithRelations } from '../types/podcasts';

export const useAudioPlayer = () => {
  const [currentPodcast, setCurrentPodcast] = useState<PodcastWithRelations | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [previousVolume, setPreviousVolume] = useState(1);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const audio = new Audio();
    audio.src = ''; // Initialize with empty source to prevent initial error
    audioRef.current = audio;

    const clearLoadingTimeout = () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
    };

    audio.addEventListener('timeupdate', () => {
      setCurrentTime(audio.currentTime);
    });

    audio.addEventListener('loadedmetadata', () => {
      setDuration(audio.duration);
      clearLoadingTimeout();
      setLoadingAudio(false);
    });

    audio.addEventListener('canplay', () => {
      clearLoadingTimeout();
      setLoadingAudio(false);
    });

    audio.addEventListener('playing', () => {
      clearLoadingTimeout();
      setIsPlaying(true);
      setLoadingAudio(false);
    });

    audio.addEventListener('pause', () => {
      clearLoadingTimeout();
      setIsPlaying(false);
      setLoadingAudio(false);
    });

    audio.addEventListener('ended', () => {
      clearLoadingTimeout();
      setIsPlaying(false);
      setLoadingAudio(false);
      setCurrentTime(0);
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
      }
    });

    audio.addEventListener('error', () => {
      // Only log error if there's an actual source set
      if (audio.src && audio.src !== window.location.href) {
        console.error('Audio error:', audio.error);
      }
      clearLoadingTimeout();
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
      setLoadingAudio(false);
      setIsPlaying(false);
      setCurrentPodcast(null);
    });

    return () => {
      clearLoadingTimeout();
      // Abort any pending play requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = ''; // Clear source before cleanup
        audioRef.current = null;
      }
      resetPlayerState();
    };
  }, []);

  const handlePlayPause = async (podcast: PodcastWithRelations) => {
    if (!audioRef.current) {
      console.error('Audio reference not available');
      resetPlayerState();
      return;
    }

    // Validate podcast and audio URL
    if (!podcast?.audio_url) {
      console.error('Invalid podcast or missing audio URL:', podcast);
      resetPlayerState();
      return;
    }

    const audioUrl = podcast.audio_url.trim();
    if (!audioUrl) {
      console.error('Empty audio URL');
      resetPlayerState();
      return;
    }

    try {
      // Clear any existing loading timeout
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }

      // Abort any pending play requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new AbortController for this play request
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      // If clicking the same podcast that's playing
      if (currentPodcast?.id === podcast.id) {
        if (isPlaying) {
          audioRef.current.pause();
          setIsPlaying(false);
        } else {
          setLoadingAudio(true);
          // Verify if the source is still valid
          if (!audioRef.current.src || audioRef.current.src !== audioUrl) {
            audioRef.current.src = audioUrl;
          }
          try {
            await audioRef.current.play();
            if (!signal.aborted) {
              setIsPlaying(true);
            }
          } catch (error) {
            if (!signal.aborted) {
              console.error('Error playing audio:', error);
              resetPlayerState();
            }
          }
        }
        return;
      }

      // If it's a new podcast
      setLoadingAudio(true);
      setCurrentPodcast(podcast);

      // Stop any current playback
      audioRef.current.pause();
      audioRef.current.src = audioUrl; // Set new source directly
      
      try {
        await audioRef.current.play();
        if (!signal.aborted) {
          setIsPlaying(true);
        }
      } catch (error) {
        if (!signal.aborted) {
          console.error('Error playing audio:', error);
          resetPlayerState();
        }
      }

    } catch (error) {
      console.error('Error handling playback:', error);
      resetPlayerState();
    }
  };

  const handleTimeUpdate = (value: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = value;
      setCurrentTime(value);
    }
  };

  const handleVolumeChange = (value: number) => {
    if (audioRef.current) {
      audioRef.current.volume = value;
      setVolume(value);
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      if (volume > 0) {
        setPreviousVolume(volume);
        handleVolumeChange(0);
      } else {
        handleVolumeChange(previousVolume);
      }
    }
  };

  // Helper function to reset player state
  const resetPlayerState = () => {
    setLoadingAudio(false);
    setIsPlaying(false);
    setCurrentPodcast(null);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current.load(); // Force reload to clear any errors
    }
  };

  return {
    currentPodcast,
    isPlaying,
    loadingAudio,
    handlePlayPause,
    audioControls: {
      currentTime,
      duration,
      volume,
      handleTimeUpdate,
      handleVolumeChange,
      toggleMute
    }
  };
};